import React, { useEffect, useState, memo } from 'react';
import {
    Grid2,
    Box,
    Typography,
    IconButton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CustomIcon from '../../../Components/CustomIcon';
import CloseButton from '../../../Components/CloseButton';
import { IDDisplayBox } from '../../../Theme/DigitalTwinStyles';
import { Divider } from 'antd';
import FullScreenDialog from '../../../Components/FullScreenDialog';
import AlertChartSection from './AlertChartSection';
import CustomTag from '../../../Components/CustomTag';
import HorizontalTimeline from '../../../Components/HorizontalTimeline';
import AlertConditionsDropdown from './AlertConditionsDropdown';
import { getAlertInfo } from '../../../Api/DigitalTwin';
import { toTitleCase } from '../../../Helper/Formatters/TextFormatter';
import { getMetricTimeStamp } from '../../../Helper/DatePicker/DateConverter';
import { getEffectedAlertEntity } from '../../../Helper/DigitalTwin/DigitalTwinFunctions';
import { alertTagStyles } from '../../../Helper/DigitalTwin/DataFile';
import { getFormattedResolutionTime } from '../../../Helper/DatePicker/DateFormatters';
import DotSeparator from '../../../Components/DotSeparator';

const SingleAlertAnalysis = ({ alertStateId }) => {
    const [open, setOpen] = useState(false);
    const { t } = useTranslation(['common', 'digital_twin'])
    const [isLoading, setIsLoading] = useState(false)
    const [timelineData, setTimelineData] = useState({})
    const [alertInfo, setAlertInfo] = useState({})

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const setupTimelineData = (alertInfo) => {
        let timeLineDateFormat = 'Do MMM YYYY h:mm A'
        let timeLineDateFormatRaw = 'YYYY-MM-DD HH:mm:ss'
        if (!alertInfo) {
            return false
        }

        let start = {
            date: getMetricTimeStamp(alertInfo.activatedAt, timeLineDateFormatRaw),
            label: "Started",
            subLabel: getMetricTimeStamp(alertInfo.activatedAt, timeLineDateFormat),
        }

        let endDate = getMetricTimeStamp(new Date(), timeLineDateFormatRaw)
        let endLabel = "Ongoing"
        let endSubLabel = getMetricTimeStamp(new Date(), timeLineDateFormat)

        if (alertInfo.resolvedAt) {
            endDate = getMetricTimeStamp(alertInfo.resolvedAt, timeLineDateFormatRaw)
            endLabel = "Resolved"
            endSubLabel = getMetricTimeStamp(alertInfo.resolvedAt, timeLineDateFormat)
        }

        if (alertInfo.expiredAt) {
            endDate = getMetricTimeStamp(alertInfo.expiredAt, timeLineDateFormatRaw)
            endLabel = "Expired"
            endSubLabel = getMetricTimeStamp(alertInfo.expiredAt, timeLineDateFormat)
        }

        let timelineSeparators = []
        if (alertInfo.openedAt) {
            timelineSeparators.push({
                date: getMetricTimeStamp(alertInfo.openedAt, timeLineDateFormatRaw),
                label: "Acknowledged",
                subLabel: getMetricTimeStamp(alertInfo.openedAt, timeLineDateFormat),
            })
        }


        let end = {
            date: endDate,
            label: endLabel,
            subLabel: endSubLabel,
        }

        setTimelineData({ start: start, timelineSeparators: timelineSeparators, end: end })

    }

    const setupAlertInformation = async () => {
        setIsLoading(true)
        const alertInfoResponse = await getAlertInfo(alertStateId)
        const alertInfo = alertInfoResponse?.response?.data

        if (alertInfo) {
            setupTimelineData(alertInfo)
            setAlertInfo(alertInfo)
        }
        setIsLoading(false)
    }

    const getTagStyles = (key, value) => {
        return alertTagStyles[key][value?.toLowerCase()] || {}
    };

    useEffect(() => {
        if (!open) {
            return;
        }
        setupAlertInformation()
    }, [alertStateId, open])


    return (
        <Box>
            <Grid2 container sx={{ display: 'flex', justifyContent: 'center' }}>
                <IconButton onClick={handleOpen} sx={{ '&:hover': { 'svg path': { fill: '#6D72F6' } } }}>
                    <CustomIcon name="binocular-icon" />
                </IconButton>
            </Grid2>
            <FullScreenDialog open={open} handleClose={handleClose} loading={isLoading}>
                <Grid2 container justifyContent={'space-between'} alignItems={'center'} sx={{ pt: 3, pb: 1.5, px: 3 }}>
                    <Grid2 container alignItems={'center'} gap={1} >
                        <Typography variant="fw500s16lh20c000000" sx={{ lineHeight: '20px' }}>{t(alertInfo.alertDescription)}</Typography>
                        <AlertConditionsDropdown alertInfo={alertInfo} />
                    </Grid2>

                    <Grid2 container justifyContent={'center'} alignItems={'center'} gap={1.8}>
                        <CloseButton onClick={handleClose} />
                    </Grid2>
                </Grid2>

                <Grid2 container alignItems={'center'} gap={1.2} sx={{ px: 3 }}>
                    <CustomTag
                        icon="dot-icon"
                        text={toTitleCase(alertInfo.alertStatus)}
                        fontColor={getTagStyles('alertStatus', alertInfo.alertStatus).fontColor}
                        backgroundColor={getTagStyles('alertStatus', alertInfo.alertStatus).backgroundColor}
                        fontSize={'12px'}
                        iconProps={{
                            sx: { color: getTagStyles('alertStatus', alertInfo.alertStatus).iconColor, fontSize: '9px' }
                        }}
                        containerStyle={{
                            borderRadius: '12px',
                        }}
                    />
                    <CustomTag
                        text={alertInfo.severityName}
                        backgroundColor={getTagStyles('severityName', alertInfo.severityName).backgroundColor}
                        fontColor={getTagStyles('severityName', alertInfo.severityName).fontColor}
                        fontSize={'12px'}
                    />

                    <IDDisplayBox sx={{ py: 0 }}>
                        <Typography variant='fw400s12c494949' sx={{ lineHeight: '24px !important' }}> {t('device_id')}: {alertInfo.deviceID}  </Typography>
                        <DotSeparator color="#000000" />
                        <Typography variant='fw500s12c000' sx={{ lineHeight: '16px' }}>{t('Entity affected')}: {getEffectedAlertEntity(alertInfo.hierarchy, alertInfo.affectedEntity)}   </Typography>

                    </IDDisplayBox>
                </Grid2>

                <Divider />
                <Grid2 container alignItems={'center'} sx={{ px: 4 }}>
                    <Grid2 container alignItems={'center'} gap={1}>
                        <Typography variant="fw500s14lc000000">{t('Alert Lifecycle')}</Typography>
                        <IDDisplayBox sx={{ py: 0, px: 1 }}>
                            <CustomIcon name="clock" stroke="#686868" />
                            <Typography variant='fw500s12c686868' sx={{ lineHeight: '24px !important' }}>{getFormattedResolutionTime(alertInfo.alertLifeTime)} </Typography>
                        </IDDisplayBox>
                    </Grid2>
                    <HorizontalTimeline data={timelineData} />
                </Grid2>

                <Divider />

                <Grid2 container>
                    <AlertChartSection
                        alertInfo={alertInfo}
                        batteryID={alertInfo.batteryID}
                        alertStateId={alertStateId}
                    />
                </Grid2>

            </FullScreenDialog>
        </Box>
    );
};

export default SingleAlertAnalysis;
